import React from 'react';
import { Button } from '@/ui/Button';
import { Label } from '@/ui/label';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/shared/components";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/components";
import { Check, ChevronsUpDown } from 'lucide-react';
import { Repository } from '@/shared/services/supabaseService';
import { cn } from '@/lib/utils';

interface RepoSelectionComboboxProps {
  repositories: Repository[];
  selectedRepositories: string[];
  onSelect: (id: string) => void;
  open: boolean;
  setOpen: (open: boolean) => void;
}

export const RepoSelectionCombobox: React.FC<RepoSelectionComboboxProps> = ({
  repositories,
  selectedRepositories,
  onSelect,
  open,
  setOpen
}) => {
  return (
    <div className="space-y-2">
      <Label className="font-mono text-xs uppercase text-muted-foreground font-bold">Select Repositories</Label>
      <p className="text-xs text-muted-foreground font-mono mb-2">
        Choose which repositories should use this rule
      </p>

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between rounded-none font-mono text-xs h-10 border-neutral-950 dark:border-neutral-100"
          >
            {selectedRepositories.length === 0
              ? "Select repositories..."
              : `${selectedRepositories.length} repository(ies) selected`
            }
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full p-0 rounded-none border-neutral-950 dark:border-neutral-100" align="start">
          <Command className="rounded-none">
            <CommandInput placeholder="Search repository..." className="font-mono text-xs rounded-none" />
            <CommandList>
              <CommandEmpty className="py-4 text-center text-xs font-mono text-muted-foreground">No repository found.</CommandEmpty>
              <CommandGroup>
                {repositories.map((repo) => {
                  const isSelected = selectedRepositories.includes(repo.id);
                  return (
                    <CommandItem
                      key={repo.id}
                      value={repo.fullName}
                      onSelect={() => onSelect(repo.id)}
                      className="font-mono text-xs cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-900 aria-selected:bg-neutral-100 dark:aria-selected:bg-neutral-900"
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          isSelected ? "opacity-100" : "opacity-0"
                        )}
                      />
                      <div className="flex flex-col">
                        <span className="font-bold">{repo.name}</span>
                        <span className="text-[10px] text-muted-foreground">{repo.fullName}</span>
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};
